import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { relativeTime } from '../utils';
import { nameFromEmail } from '../people';
import { A_PAGE, at } from '../paging';
import { PageSize, Pages } from './Pages';
import type { BoardRequest } from '../types';
import Info from './Info';

/*
 * ── ASKING, AND ANSWERING — #359 ─────────────────────────────────────────────
 *
 * Only admins invite (the ICT manager's decision, 4 September 2026). Everyone
 * else who runs a board and needs a colleague or an assistant added ASKS, and an
 * admin acts on it. This file is both sides of that:
 *
 *   RequestToAdd   — the near side: a developer files a request and watches it.
 *   RequestsQueue  — the far side: an admin's queue of what is waiting on them.
 *
 * The credential never passes through the admin. A PERSON the admin adds outright
 * on approval; an ASSISTANT the admin only approves, and the requester finishes
 * it here — the one-time password lands on THEIR screen, on the approved card
 * below, and never the admin's. See migration 0078 for why the table is shaped
 * that way.
 */

const ROLES: BoardRequest['role'][] = ['viewer', 'reviewer', 'member'];

/** The board's name, or a short of its id if it has somehow gone. */
function useBoardName(boardId: string): string {
  const projects = useApp((s) => s.projects);
  return projects.find((p) => p.id === boardId)?.name ?? 'this board';
}

/*
 * ── THE NEAR SIDE ────────────────────────────────────────────────────────────
 * A developer files a request and watches its outcome. `kind` is fixed by where
 * this sits — a person on People and access, an assistant on the Assistants
 * screen — so there is no toggle to get wrong.
 */
export function RequestToAdd({ kind, boardId }: { kind: BoardRequest['kind']; boardId: string }) {
  const me = useApp((s) => s.user?.email?.toLowerCase() ?? '');
  const admins = useApp((s) => s.access.admins);
  const requests = useApp((s) => s.boardRequests);
  const requestToAdd = useApp((s) => s.requestToAdd);
  const boardName = useBoardName(boardId);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<BoardRequest['role']>('member');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  /* Which of my own decided requests I have cleared from this view — from the
     store (kept per browser in seen.ts), so the list and the People-and-access
     nav badge read one source and drop a row together. Clearing tidies my list;
     it never deletes the record the admin still keeps. */
  const cleared = useApp((s) => s.requestsCleared);
  const clearRequest = useApp((s) => s.clearRequest);

  const isAssistant = kind === 'assistant';
  const address = email.trim().toLowerCase();
  const looksLikeAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

  /* Mine, on this board, of this kind — newest first, which the store already
     sorts them into. Fulfilled ones drop away; there is nothing left to do. A
     decided one I have cleared drops away too — for me, not for the admin. */
  const mine = requests.filter(
    (r) =>
      r.requestedBy === me &&
      r.boardId === boardId &&
      r.kind === kind &&
      r.status !== 'fulfilled' &&
      !cleared[r.id]
  );

  const file = async () => {
    setBusy(true);
    setErr('');
    const problem = await requestToAdd({
      boardId,
      kind,
      email: address,
      displayName: displayName.trim(),
      role,
      note,
    });
    setBusy(false);
    if (problem) {
      setErr(problem);
      return;
    }
    setEmail('');
    setDisplayName('');
    setNote('');
  };

  return (
    <section className="panel">
      <h2>Ask an admin to add {isAssistant ? 'an assistant' : 'someone'}</h2>
      <p className="hint">
        Only an admin can add {isAssistant ? 'an assistant' : 'a person'} to a board. Fill this in
        and it goes to your admins — you'll see the outcome here.
      </p>

      {/*
        G8 — NO ADMIN TO ACT ON IT. This should not happen: a company running
        FlightDeck has an ICT department, and an admin with it. Said plainly and
        quietly all the same, because a request into an empty queue that never
        moves is worse than being told up front.
      */}
      {admins.length === 0 && (
        <p className="req-none-admin">
          No admin is set up on this workspace yet, so there is nobody to act on this. Ask your ICT
          department to make someone an admin first.
        </p>
      )}

      <div className="req-form">
        <label className="stack">
          {/* Heading and its bracketed hint share ONE line, wrapped together so
              the label is a single flex row. Left them stacking and the hint
              became a second row, dropping this column's input a line below the
              one beside it — the boxes no longer on the same plane (Vinnie, 4
              Sept). One row here, one row there, and the inputs align. */}
          <span className="req-lab">
            {isAssistant ? 'Assistant name' : 'Their name'} <span className="req">*</span>
          </span>
          <input
            className="input"
            value={displayName}
            placeholder={isAssistant ? 'Nightly build bot' : 'Pat Moore'}
            aria-required="true"
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="stack">
          {isAssistant ? (
            <Info
              className="req-lab"
              title="Address it's known by"
              what="It need not be a real inbox — an assistant is identified by this address, not emailed at it."
            >
              Address it&rsquo;s known by <span className="req">*</span>
            </Info>
          ) : (
            <span className="req-lab">
              Their email <span className="req">*</span>
            </span>
          )}
          <input
            className="input"
            value={email}
            placeholder={isAssistant ? 'nightly@your-company.bots' : 'pat@your-company.com'}
            aria-required="true"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(e) => {
              setEmail(e.target.value);
              setErr('');
            }}
          />
        </label>
        <label className="stack">
          <span className="req-lab">Role on {boardName}</span>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as BoardRequest['role'])}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="stack">
          <Info
            className="req-lab"
            title="Why"
            what="The admin who approves this request sees what you write here."
          >
            Why
          </Info>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>

      <p className="req-note-line">
        {isAssistant ? (
          <>
            An admin approves this; you then finish setup here and its one-time password is shown to{' '}
            <strong>you</strong>, never the admin.
          </>
        ) : (
          <>Approving it adds them to {boardName} and, if they are new, sends them an invitation.</>
        )}
      </p>

      {err && <p className="field-error">{err}</p>}
      <div className="btn-row">
        <button className="btn btn-primary" disabled={busy || !looksLikeAddress || !displayName.trim()} onClick={file}>
          {busy ? 'Sending…' : `Send to ${admins.length === 1 ? 'Admin' : 'Admins'}`}
        </button>
      </div>

      {mine.length > 0 && (
        <div className="req-mine">
          <h3 className="req-mine-head">Your Requests</h3>
          {mine.map((r) => (
            <MyRequestRow key={r.id} req={r} onDismiss={() => clearRequest(r.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

/** One of the asker's own requests, in whatever state it has reached. */
function MyRequestRow({ req, onDismiss }: { req: BoardRequest; onDismiss: () => void }) {
  const withdrawRequest = useApp((s) => s.withdrawRequest);
  const fulfilAssistantRequest = useApp((s) => s.fulfilAssistantRequest);
  const boardName = useBoardName(req.boardId);

  /* A decision has been made once it is no longer pending — and every decision
     here is an admin's, so this is the "a decision from an admin" mark #359
     asks for. It stays red until the requester clears the row. */
  const decided = req.status !== 'pending';
  /* Clearable once there is nothing left for the requester to do: a decline is
     final, an approved person is being added by the admin. An approved
     assistant still has its setup to finish, so it keeps its button until it
     becomes fulfilled and drops from the list on its own. */
  const canClear = req.status === 'declined' || (req.status === 'approved' && req.kind === 'person');

  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState('');
  /* The one-time password, held here so the row that produced it cannot take it
     away by re-rendering — the same lesson as AssistantPage. */
  const [handed, setHanded] = useState('');
  const [copied, setCopied] = useState(false);

  const finish = async () => {
    setBusy(true);
    setTrouble('');
    try {
      const password = await fulfilAssistantRequest(req.id);
      if (!password) {
        setTrouble('The account was made, but no password came back. Try again.');
        return;
      }
      setHanded(password);
    } catch (e) {
      setTrouble((e as { message?: string })?.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`req-row${req.status === 'approved' ? ' is-approved' : ''}${decided ? ' is-decided-mine' : ''}`}>
      {/* The X in the top-right corner — clear a decided request from your own
          list once it is settled. It hides the row for you; the row and the
          admin's Decided log are untouched (#359). */}
      {canClear && (
        <button
          className="req-x"
          aria-label="Clear this from your requests"
          title="Clear this from your requests"
          onClick={onDismiss}
        >
          ×
        </button>
      )}
      <div className="req-row-top">
        <div className={`req-av ${req.kind === 'assistant' ? 'is-bot' : 'is-q'}`}>
          {req.kind === 'assistant' ? '◆' : '?'}
        </div>
        <div className="req-row-id">
          <div className="req-row-name">
            {req.displayName || req.email} <span className={`kind kind-${req.kind}`}>{req.kind}</span>
            {/* The red notification mark: a decision from an admin the requester
                has not cleared yet (#359). */}
            {decided && <span className="req-new">New</span>}
          </div>
          <div className="req-row-sub">
            {req.email} · {req.role} on {boardName}
          </div>

          {req.status === 'pending' && (
            <div className="req-status">
              <span className="dot-s dot-pending" /> Pending — sent {relativeTime(req.createdAt)}
            </div>
          )}
          {req.status === 'approved' && (
            <div className="req-status">
              <span className="dot-s dot-approved" />{' '}
              <strong className="ink-brand">Approved{req.decidedBy ? ` by ${nameFromEmail(req.decidedBy)}` : ''}</strong>{' '}
              {/* A person at `approved` is only a passing state while the admin
                  adds them (or a failed add being retried) — never a step of
                  the requester's, so it offers nothing to finish. Only an
                  assistant is finished here. */}
              {req.kind === 'assistant' ? '— finish setup to create its account' : '— being added'}
            </div>
          )}
          {req.status === 'declined' && (
            <div className="req-status">
              <span className="dot-s dot-declined" /> Declined{req.decidedBy ? ` by ${nameFromEmail(req.decidedBy)}` : ''}
              {req.declineReason ? ` — “${req.declineReason}”` : ''}
            </div>
          )}
        </div>

        <div className="req-row-act">
          {req.status === 'pending' && (
            <button className="btn btn-small btn-danger" disabled={busy} onClick={() => withdrawRequest(req.id)}>
              Withdraw
            </button>
          )}
          {req.status === 'approved' && req.kind === 'assistant' && !handed && (
            <button className="btn btn-small btn-primary" disabled={busy} onClick={finish}>
              {busy ? 'Creating…' : 'Finish setup →'}
            </button>
          )}
        </div>
      </div>

      {handed && (
        <div className="req-handed">
          <p className="req-handed-label">Its password — {req.email}</p>
          <div className="pw">
            <span>{handed}</span>
            <button
              className="btn btn-small"
              onClick={() => {
                navigator.clipboard?.writeText(handed);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="hint">
            This is the only time it is shown. Give it to {req.displayName || req.email} once, then it
            is done and drops off everyone's queue.
          </p>
        </div>
      )}
      {trouble && <p className="field-error">{trouble}</p>}
    </div>
  );
}

/*
 * ── THE FAR SIDE ─────────────────────────────────────────────────────────────
 * An admin's queue. Everything about who is being added is on the card, because
 * you cannot approve a request not knowing who it is for.
 */
export function RequestsQueue() {
  const requests = useApp((s) => s.boardRequests);
  const pending = requests.filter((r) => r.status === 'pending');
  const awaiting = requests.filter((r) => r.status === 'approved' && r.kind === 'assistant');

  return (
    <>
      <section className="panel">
        <h2>
          Waiting on you {pending.length > 0 && <span className="count">{pending.length}</span>}
        </h2>
        {pending.length === 0 ? (
          <p className="hint">Nothing is waiting. Requests to add a person or an assistant land here.</p>
        ) : (
          pending.map((r) => <PendingCard key={r.id} req={r} />)
        )}
      </section>

      {awaiting.length > 0 && (
        <section className="panel">
          <h2>
            Approved · awaiting the requester <span className="count">{awaiting.length}</span>
          </h2>
          {awaiting.map((r) => (
            <AwaitingCard key={r.id} req={r} />
          ))}
        </section>
      )}

      <DecidedLog requests={requests} />
    </>
  );
}

/*
 * THE DECIDED LOG — a record of what was handled, kept in sight rather than
 * vanishing. A decided request used to drop out of every section, so an admin
 * who declined one saw it disappear and could not tell it had worked, and there
 * was no record of who asked for what or why it was turned down. Declined and
 * done both stay here, newest first. (Approved assistants are still up in their
 * own lane until the requester finishes them; once done they fall to here.)
 *
 * IT IS A LIST THAT ONLY GROWS, so it carries the same way through a long one
 * as the activity and history trails do — Vinnie, 4 September: *"any screen that
 * records a list of events that can grow"* must be capped into pages, searched
 * and filtered. The shared PageSize/Pages are the #310 pair, so this reads the
 * same as everywhere else. Search and the outcome filter follow the codebase
 * rule for such controls: they appear only once the list is long enough that
 * finding one is work, because below that they are boxes with nothing to do.
 */
const ENOUGH_TO_SIFT = 8;

function DecidedLog({ requests }: { requests: BoardRequest[] }) {
  const [find, setFind] = useState('');
  const [only, setOnly] = useState<'all' | 'declined' | 'done'>('all');
  const [size, setSize] = useState(A_PAGE);
  const [page, setPage] = useState(1);

  const decided = useMemo(
    () =>
      requests
        .filter((r) => r.status === 'declined' || r.status === 'fulfilled')
        .sort((a, b) => (b.decidedAt ?? 0) - (a.decidedAt ?? 0)),
    [requests]
  );

  const matched = useMemo(() => {
    const q = find.trim().toLowerCase();
    return decided.filter((r) => {
      if (only === 'declined' && r.status !== 'declined') return false;
      if (only === 'done' && r.status !== 'fulfilled') return false;
      if (!q) return true;
      const hay = `${r.displayName ?? ''} ${r.email} ${r.declineReason ?? ''} ${
        r.decidedBy ? nameFromEmail(r.decidedBy) : ''
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [decided, only, find]);

  if (decided.length === 0) return null;

  const sift = decided.length > ENOUGH_TO_SIFT;
  const where = at(matched.length, size, page);
  const shown = matched.slice(where.start, where.end);

  return (
    <section className="panel">
      <h2>
        Decided <span className="count">{decided.length}</span>
      </h2>
      <p className="hint">A record of the requests already handled, and how.</p>

      {sift && (
        <div className="decided-sift">
          <input
            className="input decided-find"
            value={find}
            onChange={(e) => {
              setFind(e.target.value);
              setPage(1);
            }}
            placeholder="Find by name, address or reason"
          />
          <label className="decided-only">
            Showing
            <select
              value={only}
              onChange={(e) => {
                setOnly(e.target.value as 'all' | 'declined' | 'done');
                setPage(1);
              }}
            >
              <option value="all">every outcome</option>
              <option value="declined">declined</option>
              <option value="done">added or created</option>
            </select>
          </label>
          <PageSize
            size={size}
            onSize={(n) => {
              setSize(n);
              setPage(1);
            }}
          />
        </div>
      )}

      {shown.length === 0 ? (
        <p className="hint">
          {find.trim() ? `Nothing decided matches “${find.trim()}”.` : 'Nothing to show for that outcome.'}
        </p>
      ) : (
        shown.map((r) => <DecidedCard key={r.id} req={r} />)
      )}

      <Pages total={matched.length} size={size} page={where.page} onPage={setPage} what="decided" />
    </section>
  );
}

/*
 * One request already decided — read-only, the log line for it (#359).
 *
 * THE DECISION IS THE LOUD PART. Vinnie, 4 September: the old row *"is not
 * clear as what the decision is"* — it hid the verb in a status line behind a
 * small dot, level with everything else. So the outcome is now a coloured tag
 * word — Added / Created / Declined — set apart at the front, and beside it a
 * plain sentence that says who it was and what became of them. Everything else
 * — the address, who decided, when, and the reason a decline carries — drops to
 * the quiet line beneath, where it explains but no longer competes.
 */
function DecidedCard({ req }: { req: BoardRequest }) {
  const boardName = useBoardName(req.boardId);
  const isAssistant = req.kind === 'assistant';
  const declined = req.status === 'declined';
  const by = req.decidedBy ? nameFromEmail(req.decidedBy) : '';
  const who = req.displayName || req.email;
  const tag = declined ? 'Declined' : isAssistant ? 'Created' : 'Added';
  const sentence = declined
    ? `${who} was not ${isAssistant ? 'created' : 'added'}`
    : isAssistant
      ? `${who} was created as an assistant on ${boardName}`
      : `${who} was added to ${boardName} as ${req.role}`;
  return (
    <div className={`req-row is-decided ${declined ? 'decided-no' : 'decided-yes'}`}>
      <span className={`decided-tag ${declined ? 'tag-no' : 'tag-yes'}`}>{tag}</span>
      <div className="decided-body">
        <div className="decided-line">{sentence}</div>
        <div className="req-row-sub">
          {req.email}
          {by ? ` · by ${by}` : ''}
          {req.decidedAt ? ` · ${relativeTime(req.decidedAt)}` : ''}
        </div>
        {declined && req.declineReason && (
          <div className="decided-reason">“{req.declineReason}”</div>
        )}
      </div>
    </div>
  );
}

/** One pending request, with everything an admin needs to decide it knowingly. */
function PendingCard({ req }: { req: BoardRequest }) {
  const decideRequest = useApp((s) => s.decideRequest);
  const access = useApp((s) => s.access);
  const profiles = useApp((s) => s.profiles);
  const projectRoles = useApp((s) => s.projectRoles);
  const boardName = useBoardName(req.boardId);

  const [grant, setGrant] = useState<BoardRequest['role']>(req.role);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const isAssistant = req.kind === 'assistant';
  const askerName = profiles[req.requestedBy]?.name || nameFromEmail(req.requestedBy);
  /* A person already on People and access is not a new invitation — say so, so
     an admin is not surprised by what approving does (G2). */
  const alreadyHere = !isAssistant && access.emails.includes(req.email);
  /* And already ON THIS BOARD is a request that does nothing at all — Gap 3.
     The workspace check above cannot see that; this reads the board's own
     roles. Approving would just re-set the role they already hold. */
  const alreadyOnBoard = !isAssistant && Boolean(projectRoles[req.email]?.[req.boardId]);

  const decide = async (approved: boolean) => {
    setBusy(true);
    setErr('');
    /* The admin may lift or lower the role on the card before approving (G9).
       Wrapped so nothing — a returned problem or an unexpected throw — can leave
       the button stuck on "Approving…" with the card flashed back and no word
       why. Either way the button comes back and the reason shows. */
    try {
      const problem = await decideRequest(req.id, approved, approved ? undefined : reason, approved ? grant : undefined);
      if (problem) setErr(problem);
    } catch (e) {
      setErr((e as { message?: string })?.message || 'That did not work — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="req-row">
      <div className="req-row-top">
        <div className={`req-av ${isAssistant ? 'is-bot' : 'is-q'}`}>{isAssistant ? '◆' : '?'}</div>
        <div className="req-row-id">
          <div className="req-row-name">
            {req.displayName || req.email} <span className={`kind kind-${req.kind}`}>{req.kind}</span>
          </div>
          <div className="req-row-sub">{req.email}</div>
          <div className="req-meta">
            <span>
              Board <strong>{boardName}</strong>
            </span>
            <span>
              Role <strong className={`role-tag role-${req.role}`}>{req.role}</strong>
            </span>
            <span>
              Asked by <strong>{askerName}</strong> · {relativeTime(req.createdAt)}
            </span>
          </div>

          {isAssistant ? (
            <div className="req-warn is-info">
              You <strong>approve</strong> only. {askerName} creates the account and sees the one-time
              password — it never reaches you.
            </div>
          ) : alreadyOnBoard ? (
            <div className="req-warn">Already on {boardName} — approving changes nothing.</div>
          ) : alreadyHere ? (
            <div className="req-warn">Already in the workspace — approving gives them this board's access.</div>
          ) : (
            <div className="req-warn">New to the workspace — approving sends them an invitation email.</div>
          )}

          {req.note && <div className="req-note">“{req.note}”</div>}
          {err && <p className="field-error">{err}</p>}
        </div>
      </div>

      {declining ? (
        <div className="req-decline">
          <input
            className="input"
            autoFocus
            placeholder="A short reason (they see this)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="req-actions">
            <button className="btn btn-small" disabled={busy} onClick={() => setDeclining(false)}>
              Cancel
            </button>
            <button className="btn btn-small btn-danger" disabled={busy} onClick={() => decide(false)}>
              {busy ? 'Declining…' : 'Decline'}
            </button>
          </div>
        </div>
      ) : (
        <div className="req-actions">
          <span className="req-grant">
            {isAssistant ? 'Grant' : 'Add as'}{' '}
            <select value={grant} onChange={(e) => setGrant(e.target.value as BoardRequest['role'])}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </span>
          <span className="req-actions-grow" />
          <button className="btn btn-small" disabled={busy} onClick={() => setDeclining(true)}>
            Decline…
          </button>
          <button className="btn btn-small btn-primary" disabled={busy} onClick={() => decide(true)}>
            {busy ? 'Approving…' : isAssistant ? 'Approve' : 'Approve & add'}
          </button>
        </div>
      )}
    </div>
  );
}

/** An approved assistant the requester has not yet finished — kept in sight so
    it isn't lost (G4). */
function AwaitingCard({ req }: { req: BoardRequest }) {
  const profiles = useApp((s) => s.profiles);
  const askerName = profiles[req.requestedBy]?.name || nameFromEmail(req.requestedBy);
  return (
    <div className="req-row is-approved">
      <div className="req-row-top">
        <div className="req-av is-bot">◆</div>
        <div className="req-row-id">
          <div className="req-row-name">
            {req.displayName || req.email} <span className="kind kind-assistant">assistant</span>
          </div>
          <div className="req-row-sub">{req.email}</div>
          <div className="req-status">
            <span className="dot-s dot-approved" /> Approved{req.decidedAt ? ` ${relativeTime(req.decidedAt)}` : ''} —{' '}
            <strong>{askerName} hasn't finished setup yet</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
